import React, { useMemo, useCallback } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  EdgeChange,
  NodeChange,
  applyNodeChanges,
  applyEdgeChanges,
  MarkerType
} from 'reactflow';
import 'reactflow/dist/style.css';
import { 
  ConversationDialogueState, 
  CONVERSATION_STATE_TRANSITIONS,
  CONVERSATION_STATE_METADATA 
} from '../../DialogueManagementConversation/ConversationDialogueBlueprint';

interface ConversationGraphProps {
  currentState?: ConversationDialogueState;
  stateHistory?: ConversationDialogueState[];
}

const ConversationGraph: React.FC<ConversationGraphProps> = ({ currentState, stateHistory = [] }) => {
  const initialNodes = useMemo(() => {
    const nodeMap = new Map<string, Node>();
    const primaryEdges: Edge[] = [];
    const escapeEdges: Edge[] = [];
    
    // Define the main conversation flow path (left to right)
    const mainPath = [
      'conv_welcome',
      'conv_task_simple_splitting_and_confirmation',
      'conv_task_processing',
      'are_there_more_tasks',
      'assignement_additional_comments',
      'final_confirmation',
      'conv_bye'
    ];
    
    // Define escape/side states (positioned above/below main path)
    const escapeStates = [
      'conv_help',
      'conv_task_request',
      'conv_something_else',
      'user_requests_correction',
      'change_a_task',
      'delete_a_task'
    ];
    
    // Define common escape actions that appear in multiple states
    const commonEscapeActions = ['USER_SAYS_GOODBYE', 'USER_SAYS_SOMETHING_ELSE', 'USER_SAYS_HELLO'];
    
    // Position main path nodes vertically (top -> down)
    const baseX = 300;
    const spacingY = 220;
    mainPath.forEach((state, index) => {
      const metadata = CONVERSATION_STATE_METADATA[state as ConversationDialogueState];
      const isCurrent = state === currentState;
      const isVisited = stateHistory.includes(state as ConversationDialogueState);
      
      // Determine visual style based on state
      let background, border, fontWeight;
      if (isCurrent) {
        // Current state - bright blue with pulsing effect
        background = '#e1f5fe';
        border = '3px solid #2196f3';
        fontWeight = 'bold';
      } else if (isVisited) {
        // Visited state - light green
        background = '#e8f5e8';
        border = '2px solid #4caf50';
        fontWeight = 'normal';
      } else if (metadata?.isTerminal) {
        // Terminal state - purple
        background = '#f3e5f5';
        border = '2px solid #9c27b0';
        fontWeight = 'normal';
      } else {
        // Unvisited state - white/gray
        background = '#fff';
        border = '1px solid #ccc';
        fontWeight = 'normal';
      }
      
      nodeMap.set(state, {
        id: state,
        type: 'default',
        position: { 
          x: baseX, 
          y: index * spacingY + 40 
        },
        data: { 
          label: state.replace('conv_', '').replace(/_/g, ' '),
          metadata: metadata?.entryPromptKey || '',
          isTerminal: metadata?.isTerminal || false,
          isCurrent,
          isVisited,
          hasEscapeActions: Object.keys(CONVERSATION_STATE_TRANSITIONS[state as ConversationDialogueState] || {})
            .some(action => commonEscapeActions.includes(action))
        },
        style: {
          background,
          border,
          borderRadius: '8px',
          padding: '10px',
          fontSize: '12px',
          fontWeight,
          minWidth: '120px',
          boxShadow: isCurrent ? '0 0 10px rgba(33, 150, 243, 0.3)' : 'none',
          transition: 'all 0.3s ease'
        }
      });
    });
    
    // Position escape states to the left/right of the main path
    escapeStates.forEach((state, index) => {
      const metadata = CONVERSATION_STATE_METADATA[state as ConversationDialogueState];
      const isCurrent = state === currentState;
      const isVisited = stateHistory.includes(state as ConversationDialogueState);
      
      // Group correction states on left, others on right
      const isCorrectionState = ['user_requests_correction', 'change_a_task', 'delete_a_task'].includes(state);
      const yOffset = index * spacingY + 40;
      const xOffset = isCorrectionState ? baseX - 220 : baseX + 300;
      
      // Determine visual style based on state
      let background, border, fontWeight;
      if (isCurrent) {
        // Current state - bright blue with pulsing effect
        background = '#e1f5fe';
        border = '3px solid #2196f3';
        fontWeight = 'bold';
      } else if (isVisited) {
        // Visited state - light green
        background = '#e8f5e8';
        border = '2px solid #4caf50';
        fontWeight = 'normal';
      } else if (metadata?.isTerminal) {
        // Terminal state - purple
        background = '#f3e5f5';
        border = '2px solid #9c27b0';
        fontWeight = 'normal';
      } else {
        // Unvisited state - white/gray
        background = '#fff';
        border = '1px solid #ccc';
        fontWeight = 'normal';
      }
      
      nodeMap.set(state, {
        id: state,
        type: 'default',
        position: { 
          x: xOffset, 
          y: yOffset 
        },
        data: { 
          label: state.replace(/_/g, ' '),
          metadata: metadata?.entryPromptKey || '',
          isTerminal: metadata?.isTerminal || false,
          isCurrent,
          isVisited,
          hasEscapeActions: Object.keys(CONVERSATION_STATE_TRANSITIONS[state as ConversationDialogueState] || {})
            .some(action => commonEscapeActions.includes(action))
        },
        style: {
          background,
          border,
          borderRadius: '8px',
          padding: '10px',
          fontSize: '12px',
          fontWeight,
          minWidth: '120px',
          boxShadow: isCurrent ? '0 0 10px rgba(33, 150, 243, 0.3)' : 'none',
          transition: 'all 0.3s ease'
        }
      });
    });
    
    // Helper to detect escape triggers robustly
    const isEscapeTrigger = (trigger: string) => {
      if (commonEscapeActions.includes(trigger)) return true;
      const normalized = trigger.toUpperCase();
      return (
        normalized.includes('SOMETHING_ELSE') ||
        normalized.includes('GOODBYE') ||
        normalized.includes('HELLO')
      );
    };

    // Create primary flow edges (non-escape actions)
    Object.entries(CONVERSATION_STATE_TRANSITIONS).forEach(([fromState, transitions]) => {
      Object.entries(transitions).forEach(([trigger, toState]) => {
        if (toState && !isEscapeTrigger(trigger)) {
                  primaryEdges.push({
                    id: `${fromState}-${trigger}-${toState}`,
                    source: fromState,
                    target: toState,
                    label: trigger.replace('USER_', '').replace(/_/g, ' ').toLowerCase().substring(0, 15),
                    type: 'smoothstep',
                    style: {
                      stroke: '#2196f3',
                      strokeWidth: 2,
                      fontSize: '10px'
                    },
                    labelStyle: {
                      fontSize: '11px',
                      fill: '#1976d2',
                      fontWeight: 'bold',
                      background: 'white',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      border: '1px solid #e0e0e0',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    },
                    labelBgStyle: {
                      fill: 'white',
                      fillOpacity: 0.95,
                      stroke: '#e0e0e0',
                      strokeWidth: 1
                    },
                    labelBgPadding: [4, 8],
                    labelBgBorderRadius: 4
                  });
        }
      });
    });
    
    // Create escape action edges (dashed, lighter color) - but group common ones
    const escapeActionGroups = new Map<string, string[]>();
    
    Object.entries(CONVERSATION_STATE_TRANSITIONS).forEach(([fromState, transitions]) => {
      Object.entries(transitions).forEach(([trigger, toState]) => {
        if (toState && isEscapeTrigger(trigger)) {
          const key = `${fromState}-${toState}`;
          if (!escapeActionGroups.has(key)) {
            escapeActionGroups.set(key, []);
          }
          escapeActionGroups.get(key)!.push(trigger);
        }
      });
    });
    
    // Create grouped escape edges (no labels to avoid clutter)
    escapeActionGroups.forEach((triggers, key) => {
      const [fromState, toState] = key.split('-');
      
      escapeEdges.push({
        id: `escape-${key}`,
        source: fromState,
        target: toState,
        style: { 
          stroke: '#999', 
          strokeWidth: 1,
          strokeDasharray: '8,4',
          fontSize: '10px' 
        },
        labelStyle: { }
      });
    });
    
    return {
      nodes: Array.from(nodeMap.values()),
      // Make sure escape edges render first (under) by ordering before primary edges
      edges: [...escapeEdges, ...primaryEdges]
    };
  }, [currentState, stateHistory]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialNodes.edges);

  // Update nodes when initialNodes changes
  React.useEffect(() => {
    setNodes(initialNodes.nodes);
    setEdges(initialNodes.edges);
  }, [initialNodes, setNodes, setEdges]);

  const onNodeDoubleClick = useCallback((event: React.MouseEvent, node: Node) => {
    // Move node to a random position for demo
    const newPosition = {
      x: Math.random() * 400 + 100,
      y: Math.random() * 300 + 100
    };
    
    setNodes((nds) =>
      nds.map((n) =>
        n.id === node.id
          ? { ...n, position: newPosition }
          : n
      )
    );
  }, [setNodes]);
  
  return (
    <div style={{ width: '100%', height: '600px', border: '1px solid #ddd' }}>
      <div style={{ padding: '8px', fontSize: '12px', background: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
        <strong>Conversation Flow:</strong>
        <span style={{ color: '#2196f3', marginLeft: '8px' }}>━━━</span> Primary flow
        <span style={{ color: '#999', marginLeft: '16px' }}>┅┅┅</span> Escape actions (grouped)
        <span style={{ color: '#9c27b0', marginLeft: '16px' }}>■</span> Terminal state
        <span style={{ color: '#2196f3', marginLeft: '16px' }}>🔵</span> Current state
        <span style={{ color: '#4caf50', marginLeft: '16px' }}>🟢</span> Visited state
        <span style={{ color: '#ccc', marginLeft: '16px' }}>⚪</span> Unvisited state
        {currentState && (
          <span style={{ color: '#1976d2', marginLeft: '16px', fontWeight: 'bold' }}>
            🎯 Current: {currentState.replace('conv_', '').replace(/_/g, ' ')}
          </span>
        )}
      </div>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onNodeDoubleClick={onNodeDoubleClick}
                fitView
                nodesDraggable={true}
                nodesConnectable={false}
                elementsSelectable={true}
                defaultEdgeOptions={{
                  type: 'smoothstep',
                  animated: false,
                  style: { strokeWidth: 2 },
                  markerEnd: {
                    type: MarkerType.ArrowClosed,
                    color: '#2196f3',
                    width: 20,
                    height: 20
                  }
                }}
                attributionPosition="bottom-left"
              >
        <Background />
        <Controls />
        <MiniMap />
      </ReactFlow>
    </div>
  );
};

export default ConversationGraph;
